import Check from '@mui/icons-material/Check';
import Clear from '@mui/icons-material/Clear';
import { useState, useEffect } from 'react';
import { db } from '../../fbConfig';
import {
  IGiftTicketRequest,
  IFbMailService,
  ERequestStatus,
  TGiftTypes,
  GiftTypesHumanReadable,
} from '../../interfaces';
import CustomButton from '../misc/CustomButton';
import { collection, doc, onSnapshot, writeBatch } from 'firebase/firestore';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AccordionList from '../Accordion';
import { GIFT_TICKETS_COLL_ID, SESSIONS_COLL_ID } from '../../constants';

const REQ_TYPES = Object.values(TGiftTypes);

const REQ_STATUS_DISPLAY: {
  [key in ERequestStatus]: { sxColor: string; text: string };
} = {
  accepted: { sxColor: 'success.main', text: 'Accettate' },
  pending: { sxColor: 'warning.light', text: 'In attesa' },
  rejected: { sxColor: 'error.main', text: 'Rifiutate' },
};

const buildAcceptMessageHtml = (ticketsCount: string | undefined) => {
  // const htmlMsg = `Ciao!<br/>Abbiamo visionato la tua richiesta di accreditamento per ToVision 2023, e abbiamo approvato la tua richiesta per un totale di ${
  //   !ticketsCount || ticketsCount === ''
  //     ? 'un accesso'
  //     : `${ticketsCount} accessi`
  // } omaggio.<br/>Grazie e a presto,<br/>Lo staff di ToVision 2023.`;


  const htmlMsg = `Ciao!<br />
  Abbiamo visionato la tua richiesta di accreditamento per la finale di ToVision 2023 e abbiamo approvato la tua richiesta per un totale di ${!ticketsCount || ticketsCount === ''
      ? 'un accesso'
      : `${ticketsCount} accessi`} omaggio.<br />
  
  Puoi ritirare i tuoi accrediti a partire dalle ore 20.00 presso lo sportello “Accrediti”. L'evento avrà inizio alle ore 21.00 presso il Pala Ruffini di Torino.
  
  Per qualsiasi comunicazione o per fare richiesta di altri accrediti omaggio ti chiediamo di scrivere ad ufficiostampa@tovision.it
  
  Grazie e a prestissimo!
  Lo staff di ToVision 2023.`

  return htmlMsg;
};

const buildRefuseMessageHtml = () => {
 
  // const htmlMsg = `Ciao!<br/>Abbiamo visionato la tua richiesta di accreditamento per ToVision 2023, e purtroppo abbiamo constatato di non poterla approvare.<br/>Grazie e buona serata,<br/>Lo staff di ToVision`;
  const htmlMsg = `Ciao!<br />
  Abbiamo visionato la tua richiesta di accreditamento per la finale di ToVision 2023 e, a malincuore, non possiamo accettarla. Ti ricordiamo che è sempre possibile acquistare un biglietto sul sito www.tovision.it/biglietti<br />
  
  Se ci fosse stato qualche malinteso o per qualsiasi comunicazione ti invitiamo a scrivere ad ufficiostampa@tovision.it
  
  Grazie mille,
  Lo staff di ToVision 2023.`


  return htmlMsg;
};

interface IGiftTIcketsManagerProps {
  sessionId: string;
}
const REQUEST_STATUSES = Object.values(ERequestStatus);
const computeTotalGIftTickets = (giftTickets: IGiftTicketRequest[]) =>
  giftTickets.reduce((acc, req) => acc + (Number(req.ticketsCount) ?? 1), 0);

function GiftTicketsManager(props: IGiftTIcketsManagerProps) {
  const { sessionId } = props;

  const [giftTickets, setGiftTickets] = useState<IGiftTicketRequest[]>([]);

  useEffect(() => {
    const clearOnSnapshot = onSnapshot(
      collection(db, SESSIONS_COLL_ID, sessionId, GIFT_TICKETS_COLL_ID),
      giftTickestSnap => {
        const newGiftTickets = giftTickestSnap.docs.map(doc => ({
          ...doc.data(),
          email: doc.id,
        })) as IGiftTicketRequest[];
        setGiftTickets(newGiftTickets);
      },
      err => {
        console.error('ERROR WHEN LISTENING TO GIFT TICKETS', err);
      }
    );
    return () => {
      clearOnSnapshot();
    };
  }, [sessionId]);

  const acceptRequest = ({
    email,
    type,
    ticketsCount,
    name,
  }: IGiftTicketRequest) => {
    const acceptReqBatch = writeBatch(db);

    const reqToAccDocRef = doc(
      db,
      SESSIONS_COLL_ID,
      sessionId,
      GIFT_TICKETS_COLL_ID,
      email!
    );

    acceptReqBatch.set(
      reqToAccDocRef,
      { status: ERequestStatus.accepted },
      { merge: true }
    );

    const emailSendDocRef = doc(collection(db, 'mails'));
    const mailServiceDoc: IFbMailService = {
      to: [email!],
      message: {
        subject: 'Il tuo accredito per il ToVision 2023',
        html: buildAcceptMessageHtml(ticketsCount),
      },
    };
    acceptReqBatch.set(emailSendDocRef, mailServiceDoc);
    acceptReqBatch
      .commit()
      .then(() => {
        console.info('Request succefully accepted');
      })
      .catch(err => {
        console.error('ERROR WHEN ACCEPTING A REQUEST', err);
      });
  };

  const rejectRequest = ({
    email,
    type,
    ticketsCount,
    name,
  }: IGiftTicketRequest) => {
    const acceptReqBatch = writeBatch(db);

    const reqToAccDocRef = doc(
      db,
      SESSIONS_COLL_ID,
      sessionId,
      GIFT_TICKETS_COLL_ID,
      email!
    );

    acceptReqBatch.set(
      reqToAccDocRef,
      { status: ERequestStatus.rejected },
      { merge: true }
    );

    const emailSendDocRef = doc(collection(db, 'mails'));
    const mailServiceDoc: IFbMailService = {
      to: [email!],
      message: {
        subject: 'Il tuo accredito per il ToVision 2023',
        html: buildRefuseMessageHtml(),
      },
    };

    acceptReqBatch.set(emailSendDocRef, mailServiceDoc);
    acceptReqBatch
      .commit()
      .then(() => {
        console.info('Request succefully accepted');
      })
      .catch(err => {
        console.error('ERROR WHEN ACCEPTING A REQUEST', err);
      });
  };
  const totalGiftTicketsRequests = giftTickets.length;
  const totalRequestedGiftTickets = computeTotalGIftTickets(giftTickets);

  return (
    <Box margin={3}>
      <Typography color="primary" variant="h5" align="center" mb={2}>
        Accrediti
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1" align="center">
          Richieste:
        </Typography>
        <Box>
          {totalGiftTicketsRequests} (
          {REQUEST_STATUSES.map((status, i) => (
            <Typography
              variant="body1"
              display="inline"
              color={REQ_STATUS_DISPLAY[status].sxColor}
              key={status}
            >
              {giftTickets.filter(req => req.status === status).length}
              {i !== REQUEST_STATUSES.length - 1 && ' - '}
            </Typography>
          ))}
          )
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1" align="center">
          Biglietti:
        </Typography>
        <Box>
          {totalRequestedGiftTickets} (
          {REQUEST_STATUSES.map((status, i) => (
            <Typography
              variant="body1"
              display="inline"
              color={REQ_STATUS_DISPLAY[status].sxColor}
              key={status}
            >
              {computeTotalGIftTickets(
                giftTickets.filter(req => req.status === status)
              )}
              {i !== REQUEST_STATUSES.length - 1 && ' - '}
            </Typography>
          ))}
          )
        </Box>
      </Box>
      <AccordionList
        sx={{ mt: 2 }}
        items={REQ_TYPES.map((type, typeIdx) => {
          const requestOfType = giftTickets.filter(req => req.type === type);
          return {
            title: GiftTypesHumanReadable[type],
            disabled: requestOfType.length === 0,
            badgeColor: requestOfType.some(
              req => req.status === ERequestStatus.pending
            )
              ? REQ_STATUS_DISPLAY[ERequestStatus.pending].sxColor
              : undefined,
            content: (
              <AccordionList
                items={Object.values(ERequestStatus).map(status => {
                  const reqsPerStatus = giftTickets.filter(
                    req => req.type === type && req.status === status
                  );
                  return {
                    title: REQ_STATUS_DISPLAY[status].text,
                    sxColor: REQ_STATUS_DISPLAY[status].sxColor,
                    subTitle: `${reqsPerStatus.length}`,
                    disabled: reqsPerStatus.length === 0,
                    content: (
                      <Stack gap={2}>
                        {reqsPerStatus.map((req, i) => (
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            key={i}
                            gap={1}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration:
                                  req.status === 'rejected'
                                    ? 'line-through'
                                    : 'none',
                              }}
                            >
                              {req.name}{' '} {req.name && "-"} {req.fullName}{req.type !== "press" && <br></br>}
                              {req.ticketsCount && `Biglietti: ${req.ticketsCount} `}<br></br>
                              {req.invite && `Instagram/InviteCode/Ruolo: ${req.invite}`}
                              <br />
                              <i>{req.email} - {req.mobile}</i>
                            </Typography>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                            >
                              {req.status === 'pending' && (
                                <>
                                  <CustomButton
                                    useIconButton
                                    onClick={() => rejectRequest(req)}
                                  >
                                    <Clear />
                                  </CustomButton>
                                  <CustomButton
                                    useIconButton
                                    onClick={() => acceptRequest(req)}
                                  >
                                    <Check />
                                  </CustomButton>
                                </>
                              )}
                              {req.status === 'accepted' && (
                                <Check
                                  fontSize="large"
                                  sx={{
                                    color:
                                      REQ_STATUS_DISPLAY['accepted'].sxColor,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ),
                  };
                })}
              />
            ),
          };
        })}
      />
    </Box>
  );
}

export default GiftTicketsManager;
